// src/pages/PainelAgendamentos/Tabela/TabelaAgendamento.jsx
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import TabelaHeaderInputs from "./TabelaHeaderInputs";
import TabelaRow from "./TabelaRow";
import Paginacao from "./Paginacao";
import TabelaAgendamentoModals from "./TabelaAgendamentoModals";

const API_URL = process.env.REACT_APP_API_URL;

export default function TabelaAgendamento({ statusFiltro }) {
  const [filtro, setFiltro] = useState({
    data: "",
    hora: "",
    finalizado: "",
    empresa: "",
    colaborador: "",
    tipo_servico: "",
    tecnico: "",
    status: "",
  });

  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  const [modoEdicao, setModoEdicao] = useState(false);
  const [modoDetalhe, setModoDetalhe] = useState(false);
  const [modoExcluir, setModoExcluir] = useState(false);

  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);

  const normalizar = (str) =>
    str
      ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      : "";

  const fetchAgendamentos = async () => {
    try {
      const response = await fetch(`${API_URL}/agendamento/listar.php`);
      const json = await response.json();

      if (json.status) {
        const dadosTratados = json.dados.map((item) => ({
          id: item.id,
          data_abertura: item.data,
          hora_abertura: item.hora,
          finalizado_em: item.dataFinalizado || "0000-00-00",
          empresa: item.empresa,
          colaborador: item.responsavelEmpresa,
          tipo_servico: item.servico,
          tecnico_responsavel: item.tecnico,
          status: item.status,
          observacao: item.observacao || "",
          relato: item.relato || "",
        }));
        setDados(dadosTratados);
      } else {
        setDados([]);
      }
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
      setDados([]);
      toast.error("❌ Erro ao buscar agendamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgendamentos();
  }, []);

  const dadosFiltrados = dados.filter((d) => {
    return (
      (!filtro.data ||
        normalizar(d.data_abertura).includes(normalizar(filtro.data))) &&
      (!filtro.hora ||
        normalizar(d.hora_abertura).includes(normalizar(filtro.hora))) &&
      (!filtro.finalizado ||
        normalizar(d.finalizado_em).includes(normalizar(filtro.finalizado))) &&
      (!filtro.empresa ||
        normalizar(d.empresa).includes(normalizar(filtro.empresa))) &&
      (!filtro.colaborador ||
        normalizar(d.colaborador).includes(normalizar(filtro.colaborador))) &&
      (!filtro.tipo_servico ||
        normalizar(d.tipo_servico).includes(normalizar(filtro.tipo_servico))) &&
      (!filtro.tecnico ||
        normalizar(d.tecnico_responsavel).includes(normalizar(filtro.tecnico))) &&
      (!statusFiltro || normalizar(d.status) === normalizar(statusFiltro)) &&
      (!filtro.status ||
        normalizar(d.status).includes(normalizar(filtro.status)))
    );
  });

  const totalPaginas =
    itensPorPagina === Infinity
      ? 1
      : Math.ceil(dadosFiltrados.length / itensPorPagina);

  const inicio = (paginaAtual - 1) * itensPorPagina;
  const fim =
    itensPorPagina === Infinity ? dadosFiltrados.length : inicio + itensPorPagina;

  const dadosPaginados = dadosFiltrados.slice(inicio, fim);

  // ações
  const handleEditar = (chamado) => {
    setAgendamentoSelecionado(chamado);
    setModoEdicao(true);
  };

  const handleDetalhe = (chamado) => {
    setAgendamentoSelecionado(chamado);
    setModoDetalhe(true);
  };

  const handleExcluir = (chamado) => {
    setAgendamentoSelecionado(chamado);
    setModoExcluir(true);
  };

  const confirmarExclusao = async () => {
    if (!agendamentoSelecionado) return;

    try {
      const response = await fetch(`${API_URL}/agendamento/excluir.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: agendamentoSelecionado.id }),
      });

      const json = await response.json();
      if (json.status) {
        toast.success("✅ Agendamento excluído com sucesso!");
        fetchAgendamentos();
      } else {
        toast.error(json.mensagem || "❌ Erro ao excluir agendamento");
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      toast.error("❌ Erro ao excluir agendamento");
    } finally {
      handleFecharModal();
    }
  };

  const handleFecharModal = () => {
    setModoEdicao(false);
    setModoDetalhe(false);
    setModoExcluir(false);
    setAgendamentoSelecionado(null);
  };

  return (
    <div className="relative w-full h-full bg-white rounded-md shadow-md border border-gray-300 overflow-hidden">
      {loading ? (
        <div className="p-6 text-center text-gray-500">
          Carregando agendamentos...
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-[#3b3b3b] text-white">
                <tr>
                  <TabelaHeaderInputs filtro={filtro} setFiltro={setFiltro} />
                </tr>
              </thead>

              <AnimatePresence mode="wait">
                <motion.tbody
                  key={paginaAtual}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {dadosPaginados.length > 0 ? (
                    dadosPaginados.map((chamado) => (
                      <TabelaRow
                        key={chamado.id}
                        chamado={chamado}
                        onDetalhe={handleDetalhe}
                        onEditar={handleEditar}
                        onExcluir={handleExcluir}
                      />
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-2 py-6 text-center text-gray-500"
                      >
                        Nenhum agendamento encontrado.
                      </td>
                    </tr>
                  )}
                </motion.tbody>
              </AnimatePresence>
            </table>
          </div>

          {dadosFiltrados.length > 0 && (
            <div className="border-t border-gray-200 p-2">
              <Paginacao
                paginaAtual={paginaAtual}
                setPaginaAtual={setPaginaAtual}
                totalPaginas={totalPaginas}
                itensPorPagina={itensPorPagina}
                setItensPorPagina={setItensPorPagina}
              />
            </div>
          )}
        </div>
      )}

      {/* Modais separados */}
      <TabelaAgendamentoModals
        modoEdicao={modoEdicao}
        modoDetalhe={modoDetalhe}
        modoExcluir={modoExcluir}
        agendamentoSelecionado={agendamentoSelecionado}
        onFechar={handleFecharModal}
        onAtualizar={fetchAgendamentos}
        onConfirmarExclusao={confirmarExclusao}
      />
    </div>
  );
}
